import { forwardRef } from 'react'
import { Checkbox as BaseCheckbox } from '@base-ui-components/react/checkbox'
import { Check } from 'lucide-react'

export interface CheckboxProps {
  checked: boolean
  onChange: (checked: boolean) => void
  /** The drawn box: 24px at `md`, 32px at `lg`. Never the hit area, which is
   *  44px at both — the box is centred inside it. */
  size?: 'md' | 'lg'
  /** Round for a list that sinks — Shortlist, Saved, anything where ticking
   *  means "done with this". Square for a checklist, where the ticks stay
   *  visible and are the point (L5). */
  shape?: 'round' | 'square'
  disabled?: boolean
  /** Needed whenever the checkbox is not inside a `<label>`. */
  'aria-label'?: string
  'aria-labelledby'?: string
  className?: string
}

/**
 * Base UI's Checkbox: a span with `role="checkbox"` plus a hidden input, so it
 * takes part in a form and answers Space without the browser's own box, which
 * cannot be drawn.
 *
 * Three elements, and each has one job: the root is the 44px target, the box is
 * the 24 or 32px square or circle, the indicator is the tick. Drawing the box
 * *as* the target would mean either a 24px tap area or a 44px checkbox, and the
 * brief asks for neither.
 */
export const Checkbox = forwardRef<HTMLElement, CheckboxProps>(function Checkbox(
  { checked, onChange, size = 'md', shape = 'round', disabled, className = '', ...aria },
  ref,
) {
  const classes = [
    'u-check', `u-check--${size}`, `u-check--${shape}`, className,
  ].filter(Boolean).join(' ')

  return (
    <BaseCheckbox.Root
      ref={ref}
      checked={checked}
      onCheckedChange={next => onChange(next)}
      disabled={disabled}
      className={classes}
      {...aria}
    >
      <span className="u-check__box">
        <BaseCheckbox.Indicator className="u-check__tick">
          <Check aria-hidden="true" />
        </BaseCheckbox.Indicator>
      </span>
    </BaseCheckbox.Root>
  )
})
